import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import {
  MatSnackBar,
  MatSnackBarRef,
  TextOnlySnackBar,
} from '@angular/material/snack-bar';
import { Observable, Subscription, forkJoin, of, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface VersionManifest {
  version: string;
  buildTime?: string | null;
  environment?: string | null;
  commit?: string | null;
  branch?: string | null;
  releaseFamily?: string | null;
}

interface LatestVersionResponse {
  latestVersion: string | null;
}

interface VersionCheckResult {
  deployedVersion: string | null;
  latestBackendVersion: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class VersionService implements OnDestroy {
  private readonly versionEndpoint = '/assets/version.json';
  private readonly latestVersionEndpoint = `${environment.AUTH_API_URL}`;
  private readonly currentVersion = environment.APP_VERSION;
  private readonly currentReleaseFamily =
    environment.APP_RELEASE_FAMILY ||
    this.extractReleaseFamily(this.currentVersion);
  private readonly pollIntervalMs = 5 * 60 * 1000;
  private readonly reloadKeyPrefix = 'app:version:reloaded:';
  private readonly notifiedKeyPrefix = 'app:version:notified:';

  private initialized = false;
  private pollingSub?: Subscription;
  private routerEventsSub?: Subscription;
  private activeSnackBar?: MatSnackBarRef<TextOnlySnackBar>;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
  ) {}

  initialize() {
    if (this.initialized) return;
    this.initialized = true;

    this.routerEventsSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncPollingState());

    window.addEventListener('storage', this.onStorageChange);

    this.syncPollingState();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.routerEventsSub?.unsubscribe();
    window.removeEventListener('storage', this.onStorageChange);
  }

  private readonly onStorageChange = (event: StorageEvent) => {
    if (event.key === 'access_token' || event.key === null) {
      this.syncPollingState();
    }
  };

  private syncPollingState() {
    if (this.isAuthenticated()) {
      this.startPolling();
      return;
    }

    this.stopPolling();
  }

  private startPolling() {
    if (this.pollingSub) return;

    this.pollingSub = timer(0, this.pollIntervalMs)
      .pipe(exhaustMap(() => this.checkVersionStatus()))
      .subscribe((versionStatus) => {
        const targetVersion = this.resolveTargetVersion(versionStatus);
        if (!targetVersion) return;
        if (targetVersion === this.currentVersion) return;
        this.handleVersionMismatch(targetVersion);
      });
  }

  private stopPolling() {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
    this.activeSnackBar?.dismiss();
    this.activeSnackBar = undefined;
  }

  private checkVersionStatus(): Observable<VersionCheckResult> {
    if (!this.isAuthenticated()) {
      return of({
        deployedVersion: null,
        latestBackendVersion: null,
      });
    }

    return forkJoin({
      deployedVersion: this.fetchDeployedVersion(),
      latestBackendVersion: this.fetchLatestFrontendVersion(),
    });
  }

  private fetchDeployedVersion(): Observable<string | null> {
    const querySuffix = `t=${Date.now()}`;
    const url = `${this.versionEndpoint}?${querySuffix}`;
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    });

    return this.http.get<VersionManifest>(url, { headers }).pipe(
      catchError(() => of(null)),
      map((manifest) => {
        const version = manifest?.version?.trim();
        return version || null;
      }),
    );
  }

  private fetchLatestFrontendVersion(): Observable<string | null> {
    if (!this.currentReleaseFamily) {
      return of(null);
    }

    const querySuffix = `releaseFamily=${encodeURIComponent(
      this.currentReleaseFamily,
    )}&t=${Date.now()}`;
    const url = `${this.latestVersionEndpoint}/app-version/latest?${querySuffix}`;
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
    });

    return this.http.get<LatestVersionResponse>(url, { headers }).pipe(
      catchError(() => of({ latestVersion: null })),
      map((response) => {
        const latestVersion = response.latestVersion?.trim() ?? '';
        return this.isVersionTag(latestVersion) ? latestVersion : null;
      }),
    );
  }

  private resolveTargetVersion(
    versionStatus: VersionCheckResult,
  ): string | null {
    const { deployedVersion, latestBackendVersion } = versionStatus;

    if (
      latestBackendVersion &&
      deployedVersion &&
      latestBackendVersion === deployedVersion &&
      latestBackendVersion !== this.currentVersion
    ) {
      return latestBackendVersion;
    }

    if (deployedVersion && deployedVersion !== this.currentVersion) {
      return deployedVersion;
    }

    return null;
  }

  private handleVersionMismatch(remoteVersion: string) {
    if (!this.hasReloadAttempted(remoteVersion)) {
      this.markReloadAttempt(remoteVersion);
      this.performHardReload();
      return;
    }

    if (this.hasNotifiedForVersion(remoteVersion)) {
      return;
    }

    this.markNotifiedForVersion(remoteVersion);
    this.activeSnackBar = this.snackBar.open(
      'Uma nova versão da plataforma está disponível.',
      'Atualizar agora',
      {
        duration: 15000,
      },
    );

    this.activeSnackBar.onAction().subscribe(() => {
      this.performHardReload();
    });
  }

  private isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    return Boolean(token && token.trim());
  }

  private hasReloadAttempted(version: string): boolean {
    return sessionStorage.getItem(this.reloadStorageKey(version)) === '1';
  }

  private markReloadAttempt(version: string): void {
    sessionStorage.setItem(this.reloadStorageKey(version), '1');
  }

  private hasNotifiedForVersion(version: string): boolean {
    return sessionStorage.getItem(this.notifiedStorageKey(version)) === '1';
  }

  private markNotifiedForVersion(version: string): void {
    sessionStorage.setItem(this.notifiedStorageKey(version), '1');
  }

  private reloadStorageKey(version: string): string {
    return `${this.reloadKeyPrefix}${version}`;
  }

  private notifiedStorageKey(version: string): string {
    return `${this.notifiedKeyPrefix}${version}`;
  }

  private performHardReload() {
    const url = new URL(window.location.href);
    url.searchParams.set('app-update', Date.now().toString());
    window.location.replace(url.toString());
  }

  private isVersionTag(value: string): boolean {
    return /^\d+\.\d+\.\d+\.\d+$/.test(value);
  }

  private extractReleaseFamily(
    version: string | null | undefined,
  ): string | null {
    if (!version || !this.isVersionTag(version)) {
      return null;
    }

    const [major, minor] = version.split('.');
    return `${major}.${minor}`;
  }
}
